import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { RECAPTCHA_SITE_KEY } from '../tokens/recaptcha-site-key.token';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private readonly doc = inject(DOCUMENT);
  private readonly injectedKey = inject(RECAPTCHA_SITE_KEY, { optional: true });
  private loadPromise: Promise<void> | null = null;

  get siteKey(): string {
    return (this.injectedKey ?? '').trim();
  }

  isConfigured(): boolean {
    return this.siteKey.length > 0;
  }

  private waitForGrecaptcha(): Promise<void> {
    const win = this.doc.defaultView;
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + 15000;
      const tick = () => {
        if (win?.grecaptcha?.ready) {
          resolve();
          return;
        }
        if (Date.now() > deadline) {
          reject(new Error('reCAPTCHA load timeout'));
          return;
        }
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  private loadScript(): Promise<void> {
    if (!this.isConfigured()) {
      return Promise.reject(new Error('reCAPTCHA is not configured'));
    }

    const win = this.doc.defaultView;
    if (!win) {
      return Promise.reject(new Error('No window'));
    }

    if (win.grecaptcha?.ready) {
      return Promise.resolve();
    }

    if (this.loadPromise) {
      return this.loadPromise;
    }

    const pending = (async () => {
      let script = this.doc.querySelector<HTMLScriptElement>('script[data-recaptcha-v3]');
      if (!script) {
        script = this.doc.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(this.siteKey)}`;
        script.async = true;
        script.defer = true;
        script.setAttribute('data-recaptcha-v3', 'true');
        const insertError = new Promise<never>((_, reject) => {
          script!.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
        });
        this.doc.head.appendChild(script);
        await Promise.race([
          new Promise<void>((resolve) => {
            script!.onload = () => resolve();
          }),
          insertError,
        ]);
      }
      await this.waitForGrecaptcha();
    })();

    this.loadPromise = pending;
    pending.catch(() => {
      this.loadPromise = null;
    });

    return pending;
  }

  async execute(action: string): Promise<string> {
    await this.loadScript();
    const win = this.doc.defaultView;
    if (!win?.grecaptcha?.ready) {
      throw new Error('reCAPTCHA unavailable');
    }
    return new Promise((resolve, reject) => {
      win.grecaptcha!.ready(() => {
        win.grecaptcha!.execute(this.siteKey, { action }).then(resolve).catch(reject);
      });
    });
  }
}
